/**
 * V2Layout — shared shell for all /v2/* pages except the main app.
 *
 * Imports IconStrip and Sidebar from V2Nav (single source of truth).
 * Change V2Nav.tsx once → updates everywhere.
 */

import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { IconStrip, Sidebar, checkIsAdmin, getInitials, type OpenPanel, type SidebarVideo } from './V2Nav';
import MobileTabBar from './MobileTabBar';
import MobileMorePanel from './MobileMorePanel';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePrimateAuth } from '../hooks/usePrimateAuth';

import { getUserVideos, deleteUserVideo, getDemoVideos } from '../lib/api';
import { useNavigate } from 'react-router';

export default function V2Layout({ children }: { children: React.ReactNode }) {
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const handleSignOut = () => signOut({ redirectUrl: '/' });
  const { isPro, isAdmin: isAdminFromCtx, authLoaded } = usePrimateAuth();
  const planTier = (user?.publicMetadata as { plan?: string } | undefined)?.plan ?? 'free';

  // Check ALL email addresses, not just primary — avoids race where primaryEmailAddress
  // is null while emailAddresses is already populated.
  const allEmails = user?.emailAddresses?.map(e => e.emailAddress) ?? [];
  const primaryEmail = user?.primaryEmailAddress?.emailAddress;
  const isAdmin = isAdminFromCtx || checkIsAdmin(primaryEmail) || allEmails.some(e => checkIsAdmin(e));

  // Build display name and initials from Clerk user
  // Avoid Clerk's fullName when firstName already contains the full name (shows "Matt Miesnieks Miesnieks")
  const rawFirstName = user?.firstName ?? '';
  const rawLastName = user?.lastName ?? '';
  const hasSpaceInFirstName = rawFirstName.trim().includes(' ');
  const fullName = user
    ? (hasSpaceInFirstName ? rawFirstName.trim() : [rawFirstName, rawLastName].filter(Boolean).join(' ').trim() || user.username || null)
    : null;
  const userInitials = getInitials(fullName);
  const userName = fullName || null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPanel, setSidebarPanel] = useState<OpenPanel>(null);
  // Mobile uses the native MobileMorePanel bottom-sheet (same as the app), NOT
  // the desktop Sidebar. (2026-06-11: tapping "More" on mobile was sliding out
  // the desktop sidebar — wrong surface; aligned with the app's mobile nav.)
  const [moreOpen, setMoreOpen] = useState(false);

  // Fetch demo videos for sidebar (public, no auth required)
  const [demoVideos, setDemoVideos] = useState<SidebarVideo[]>([]);
  useEffect(() => {
    // R15 (PRI-439): endpoint literal moved to the API layer.
    getDemoVideos()
      .then(data => setDemoVideos((data.videos ?? []) as unknown as SidebarVideo[]))
      .catch(() => {});
  }, []);

  // Fetch user videos for sidebar when signed in
  const [userVideos, setUserVideos] = useState<SidebarVideo[]>([]);
  useEffect(() => {
    if (!isSignedIn || !authLoaded) return;
    getUserVideos()
      .then(data => setUserVideos(data.videos ?? []))
      .catch(() => {});
  }, [isSignedIn, authLoaded]);

  // Derive live-stream sessions from userVideos (same filter as PrimateVisionV2)
  const streamUserVideos = userVideos.filter(
    v => v.display_name?.startsWith('Live: ') || (v as { filename?: string }).filename === 'live-stream'
  );

  const navigate = useNavigate();

  const handleLiveStreamsSelect = () => {
    setSidebarOpen(false);
    navigate('/dashboard/v2');
  };

  const handleLiveStreamsNewSession = () => {
    setSidebarOpen(false);
    navigate('/dashboard/v2');
  };

  const handleLiveStreamsDelete = async () => {
    await Promise.all(streamUserVideos.map(v => deleteUserVideo(v.id)));
    setUserVideos(prev => prev.filter(v =>
      !streamUserVideos.some(sv => sv.id === v.id)
    ));
  };

  const isMobile = useIsMobile();

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'v2-nav-hide';
    style.textContent = `
      /* Hide existing top nav bar on V2 pages */
      header.fixed.top-0 { display: none !important; }
    `;
    document.head.appendChild(style);

    return () => {
      document.getElementById('v2-nav-hide')?.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Desktop nav: icon strip + slide-out sidebar. Mobile never renders these. */}
      {!isMobile && (
        <>
          <IconStrip
            onOpen={(panel) => { setSidebarPanel(panel); setSidebarOpen(true); }}
            isSignedIn={isSignedIn ?? false}
            isAdmin={isAdmin}
            userInitials={userInitials}
          />
          <Sidebar
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            isSignedIn={isSignedIn ?? false}
            isAdmin={isAdmin}
            initialPanel={sidebarPanel}
            userInitials={userInitials}
            userName={userName}
            isPro={isPro}
            planTier={planTier}
            demoVideos={demoVideos}
            userVideos={userVideos}
            streamSessionCount={streamUserVideos.length}
            onLiveStreamsSelect={handleLiveStreamsSelect}
            onLiveStreamsDelete={handleLiveStreamsDelete}
            onLiveStreamsNewSession={handleLiveStreamsNewSession}
            onSignOut={handleSignOut}
          />
        </>
      )}
      {/* Content area — offset by icon strip width */}
      <div
        data-testid="main-content"
        style={{
          marginLeft: isMobile ? 0 : 48,
          paddingBottom: isMobile ? 'calc(49px + env(safe-area-inset-bottom))' : 0,
        }}
      >
        {children}
      </div>
      {/* Mobile nav: native bottom tab bar + MobileMorePanel sheet (matches the app). */}
      {isMobile && (
        <>
          <MobileTabBar onOpenMore={() => setMoreOpen(true)} moreActive={moreOpen} />
          <MobileMorePanel
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            isSignedIn={isSignedIn ?? false}
            userInitials={userInitials}
            userName={userName ?? undefined}
            userEmail={primaryEmail ?? undefined}
            isPro={isPro}
            planTier={planTier}
            isAdmin={isAdmin}
            demoVideos={demoVideos.map(v => ({ id: v.id, display_name: v.display_name }))}
            userVideos={userVideos.map(v => ({ id: v.id, display_name: v.display_name }))}
            streamSessionCount={streamUserVideos.length}
            onLiveStreamsSelect={handleLiveStreamsSelect}
            onSignOut={handleSignOut}
          />
        </>
      )}
    </>
  );
}
